/**
 * Đo đạc — overlay Canvas 2D đè lên WebGL (port từ bản thử nghiệm).
 *  - Đo tổng thể: khung bao W×H (drawBBdims).
 *  - Đo tay: bám 26 snap point (góc + giữa cạnh + tâm mặt), trong 30px màn hình.
 *  - Đo lọt lòng: chọn 2 tấm song song → mặt-mặt, hoặc khe cạnh nếu cùng mặt phẳng (innerDim.ts).
 * Vẽ độc lập với render WebGL; đăng ký qua SceneManager.onAfterRender.
 */
import { Box3, Vector3 } from 'three';
import { SCALE } from './constants';
import type { Dimension } from '../lib/types/wvm';
import { t } from '../i18n/index.svelte';
import type { SceneManager } from './SceneManager';
import { solveInnerDim, type Axis } from './innerDim';

const S = SCALE;
const SNAP_PX = 30;
const SHORT_PX = 24; // đường đo chiếu ngắn hơn ngưỡng này = đang nhìn dọc trục đo
const TOUCH_MM = 0.5; // 2 tấm giáp mí (sai số float) vẫn coi là tách rời, khe 0

type InnerFail = 'noDir' | 'notParallel' | 'overlap';
const INNER_FAIL_MSG = {
  noDir: 'measure.innerNoDir',
  notParallel: 'measure.innerSameDir',
  overlap: 'measure.innerOverlap',
} as const;

const COLOR_TOTAL = '#0078D7';
const COLOR_MANUAL = '#FFB700';
const COLOR_INNER = '#28A745';
const COLOR_AUTHORED = '#00ACC1'; // xanh ngọc — dim do user DỰNG trong plugin (giống BAZIS)
const AUTHORED_OFF = 32; // nudge nhãn (px màn hình) khi dim KHÔNG có offset → nhãn không dính mặt gỗ

export type DimMode = 'off' | 'manual' | 'inner';

interface SavedDim {
  pt1: Vector3;
  pt2: Vector3;
  axis: Axis;
  valueMM: number;
  color: string;
}

export type PickResult =
  | { kind: 'manual'; count: number; mm: number | null }
  | { kind: 'inner'; status: string; mm: number | null }
  | null;

export class DimensionTool {
  mode: DimMode = 'off';
  showTotal = false;

  private readonly ctx: CanvasRenderingContext2D;
  private measPoints: Vector3[] = [];
  private hoverSnap: Vector3 | null = null;
  private savedDims: SavedDim[] = [];
  private innerParts: number[] = [];
  // 04b: kích thước do user dựng (từ WVM.dimensions) — toggle ĐỘC LẬP với công cụ đo.
  private authoredDims: Dimension[] = [];
  private authoredVisible = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly scene: SceneManager,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Không tạo được 2D context cho overlay đo');
    this.ctx = ctx;
  }

  // ---------- điều khiển ----------

  setMode(mode: DimMode): void {
    this.mode = mode;
    this.measPoints = [];
    this.hoverSnap = null;
    this.innerParts = [];
    this.draw();
  }

  toggleTotal(): boolean {
    this.showTotal = !this.showTotal;
    this.draw();
    return this.showTotal;
  }

  // ---------- kích thước do user dựng (04b) ----------

  /** Nạp danh sách dimension từ WVM (gọi 1 lần sau buildModel). Vắng/rỗng = không vẽ gì. */
  setAuthoredDims(dims: Dimension[]): void {
    this.authoredDims = dims;
    this.draw();
  }

  /** Bật/tắt hiển thị dimension (độc lập mode đo). Overlay 2D → draw() repaint ngay, không cần render WebGL. */
  setAuthoredVisible(on: boolean): void {
    this.authoredVisible = on;
    this.draw();
  }

  /** Có dimension để hiện không → UI quyết định có hiện nút toggle. */
  get hasAuthored(): boolean {
    return this.authoredDims.length > 0;
  }

  clearAll(): void {
    this.measPoints = [];
    this.savedDims = [];
    this.innerParts = [];
    this.hoverSnap = null;
    this.draw();
  }

  get active(): boolean {
    return this.mode !== 'off' || this.showTotal || this.authoredVisible;
  }

  // ---------- tương tác ----------

  /** Di chuột (chỉ chế độ đo tay): cập nhật điểm bám + vẽ lại overlay. */
  hover(clientX: number, clientY: number): void {
    if (this.mode !== 'manual') return;
    this.hoverSnap = this.snapAt(clientX, clientY);
    this.draw();
  }

  pick(clientX: number, clientY: number): PickResult {
    if (this.mode === 'manual') return this.pickManual(clientX, clientY);
    if (this.mode === 'inner') return this.pickInner(clientX, clientY);
    return null;
  }

  private pickManual(clientX: number, clientY: number): PickResult {
    const pt = this.snapAt(clientX, clientY) ?? this.hoverSnap;
    if (!pt) return null;
    if (this.measPoints.length >= 2) this.measPoints = [];
    this.measPoints.push(pt.clone());
    let mm: number | null = null;
    if (this.measPoints.length === 2) {
      mm = Math.round(this.measPoints[0].distanceTo(this.measPoints[1]) / S);
    }
    this.draw();
    return { kind: 'manual', count: this.measPoints.length, mm };
  }

  private pickInner(clientX: number, clientY: number): PickResult {
    const hit = this.scene.raycast(clientX, clientY);
    if (!hit) return null;
    return this.pickInnerById(hit.part.id);
  }

  /** Chọn tấm cho đo lọt lòng theo id (dùng khi bấm từ danh sách — tránh tấm bị che). */
  pickInnerById(id: number): PickResult {
    if (this.mode !== 'inner') return null;
    const i = this.innerParts.indexOf(id);
    if (i >= 0) {
      this.innerParts.splice(i, 1);
      this.draw();
      return { kind: 'inner', status: t('measure.innerPick', { n: this.innerParts.length + 1 }), mm: null };
    }
    if (this.innerParts.length >= 2) this.innerParts = [];
    this.innerParts.push(id);
    if (this.innerParts.length === 1) {
      this.draw();
      return { kind: 'inner', status: t('measure.innerPick2'), mm: null };
    }
    const res = this.calcInnerDim(this.innerParts[0], this.innerParts[1]);
    this.innerParts = [];
    if (!res.ok) {
      this.draw();
      return { kind: 'inner', status: t(INNER_FAIL_MSG[res.reason]), mm: null };
    }
    this.savedDims.push(res.dim);
    this.draw();
    const mm = res.dim.valueMM;
    const key = res.gap ? 'measure.innerGapResult' : 'measure.innerResult';
    return { kind: 'inner', status: t(key, { mm }), mm };
  }

  // ---------- snap ----------

  private snapAt(clientX: number, clientY: number): Vector3 | null {
    const hit = this.scene.raycast(clientX, clientY);
    if (!hit) return null;
    const rect = this.canvas.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const box = new Box3().setFromObject(hit.mesh);
    let nearest: Vector3 | null = null;
    let nearDist = Infinity;
    let fallback: Vector3 | null = null;
    let fbDist = Infinity;
    for (const sp of snapPoints(box)) {
      const p = this.scene.project(sp);
      const d = Math.hypot(p.x - mx, p.y - my);
      if (d < fbDist) {
        fbDist = d;
        fallback = sp;
      }
      if (d < nearDist && d < SNAP_PX) {
        nearDist = d;
        nearest = sp;
      }
    }
    return nearest ?? fallback;
  }

  /**
   * Khoảng lọt lòng giữa 2 tấm song song — theo khung bao thế giới nên nhận mọi geometry
   * (box/mesh/extrusion) và cả 3 trục (vách, kệ, cánh/hậu). Xem solveInnerDim.
   */
  private calcInnerDim(
    id1: number,
    id2: number,
  ): { ok: true; dim: SavedDim; gap: boolean } | { ok: false; reason: InnerFail } {
    const o1 = this.scene.parts.find((o) => o.part.id === id1);
    const o2 = this.scene.parts.find((o) => o.part.id === id2);
    if (!o1 || !o2) return { ok: false, reason: 'noDir' };
    const bb1 = new Box3().setFromObject(o1.mesh);
    const bb2 = new Box3().setFromObject(o2.mesh);
    const r = solveInnerDim(bb1, bb2, TOUCH_MM * S);
    if (!r.ok) return r;
    return {
      ok: true,
      gap: r.kind === 'gap',
      dim: { pt1: r.pt1, pt2: r.pt2, axis: r.axis, valueMM: Math.round(r.dist / S), color: COLOR_INNER },
    };
  }

  // ---------- vẽ overlay ----------

  draw = (): void => {
    this.resize();
    const { ctx, canvas } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (this.authoredVisible) for (const dim of this.authoredDims) this.drawAuthoredDim(dim);
    if (this.showTotal) this.drawTotal();
    for (const dim of this.savedDims) this.drawSavedDim(dim);
    this.drawManual();
  };

  /**
   * Vẽ 1 dimension user dựng (04b). Toạ độ WVM = mm hệ world với parts → nhân SCALE, KHÔNG recenter.
   * Có `offset` (world mm): đường đo dịch tới `(p+offset)*S`, vẽ 2 đường gióng từ cạnh gốc.
   * Vắng `offset`: nudge nhãn vuông góc trong KHÔNG GIAN MÀN HÌNH (như công cụ đo) để khỏi dính mặt gỗ.
   */
  private drawAuthoredDim(dim: Dimension): void {
    const label = dim.text && dim.text.trim() ? dim.text : `${Math.round(dim.value)} mm`;
    const a = this.scene.project(new Vector3(dim.p1[0] * S, dim.p1[1] * S, dim.p1[2] * S));
    const b = this.scene.project(new Vector3(dim.p2[0] * S, dim.p2[1] * S, dim.p2[2] * S));
    let e1: { x: number; y: number };
    let e2: { x: number; y: number };
    if (dim.offset) {
      const [ox, oy, oz] = dim.offset;
      e1 = this.scene.project(new Vector3((dim.p1[0] + ox) * S, (dim.p1[1] + oy) * S, (dim.p1[2] + oz) * S));
      e2 = this.scene.project(new Vector3((dim.p2[0] + ox) * S, (dim.p2[1] + oy) * S, (dim.p2[2] + oz) * S));
    } else {
      // vuông góc với đoạn a→b trên màn hình, đẩy AUTHORED_OFF px (nhãn tách khỏi cạnh đo).
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.hypot(dx, dy) || 1;
      const nx = (-dy / len) * AUTHORED_OFF;
      const ny = (dx / len) * AUTHORED_OFF;
      e1 = { x: a.x + nx, y: a.y + ny };
      e2 = { x: b.x + nx, y: b.y + ny };
    }
    const { ctx } = this;
    ctx.strokeStyle = COLOR_AUTHORED;
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(e1.x, e1.y);
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(e2.x, e2.y);
    ctx.stroke();
    ctx.setLineDash([]);
    this.dimLine(e1.x, e1.y, e2.x, e2.y, label, COLOR_AUTHORED);
  }

  private resize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    if (this.canvas.width !== w) this.canvas.width = w;
    if (this.canvas.height !== h) this.canvas.height = h;
  }

  private drawTotal(): void {
    const bb = this.scene.bounds();
    const size = new Vector3();
    bb.getSize(size);
    const W = Math.round(size.x / S);
    const H = Math.round(size.y / S);
    const z = (bb.min.z + bb.max.z) / 2;
    const tL = this.scene.project(new Vector3(bb.min.x, bb.max.y, z));
    const tR = this.scene.project(new Vector3(bb.max.x, bb.max.y, z));
    const bL = this.scene.project(new Vector3(bb.min.x, bb.min.y, z));
    const off = 36;
    const { ctx } = this;
    ctx.strokeStyle = COLOR_TOTAL;
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(tL.x, tL.y);
    ctx.lineTo(tL.x, tL.y - off);
    ctx.moveTo(tR.x, tR.y);
    ctx.lineTo(tR.x, tR.y - off);
    ctx.moveTo(tL.x, tL.y);
    ctx.lineTo(tL.x - off, tL.y);
    ctx.moveTo(bL.x, bL.y);
    ctx.lineTo(bL.x - off, bL.y);
    ctx.stroke();
    ctx.setLineDash([]);
    this.dimLine(tL.x, tL.y - off, tR.x, tR.y - off, `${W} mm`, COLOR_TOTAL);
    this.dimLine(tL.x - off, tL.y, bL.x - off, bL.y, `${H} mm`, COLOR_TOTAL);
  }

  private drawSavedDim(dim: SavedDim): void {
    const s1 = this.scene.project(dim.pt1);
    const s2 = this.scene.project(dim.pt2);
    const label = `${dim.valueMM} mm`;
    const off = 32;
    const { ctx } = this;
    ctx.strokeStyle = dim.color;
    ctx.lineWidth = 0.8;
    const dx = s2.x - s1.x;
    const dy = s2.y - s1.y;
    const len = Math.hypot(dx, dy);
    if (len < SHORT_PX) {
      // đang nhìn dọc trục đo (vd. đo theo Z ở góc chính diện) → chỉ chấm + nhãn + gợi ý xoay
      const mx = (s1.x + s2.x) / 2;
      const my = (s1.y + s2.y) / 2;
      this.dot(mx, my, dim.color);
      this.label(mx, my - 18, label, dim.color);
      this.label(mx, my + 18, t('measure.innerRotateHint'), dim.color, 11);
      return;
    }
    // đường gióng vuông góc đoạn s1→s2 trên màn hình; ưu tiên phía trên/trái như bản cũ
    let nx = -dy / len;
    let ny = dx / len;
    if (nx + ny > 0) {
      nx = -nx;
      ny = -ny;
    }
    const e1 = { x: s1.x + nx * off, y: s1.y + ny * off };
    const e2 = { x: s2.x + nx * off, y: s2.y + ny * off };
    ctx.setLineDash([3, 3]);
    ctx.beginPath();
    ctx.moveTo(s1.x, s1.y);
    ctx.lineTo(e1.x, e1.y);
    ctx.moveTo(s2.x, s2.y);
    ctx.lineTo(e2.x, e2.y);
    ctx.stroke();
    ctx.setLineDash([]);
    this.dimLine(e1.x, e1.y, e2.x, e2.y, label, dim.color);
  }

  private drawManual(): void {
    const { ctx } = this;
    // điểm đã chấm
    for (const p of this.measPoints) {
      const s = this.scene.project(p);
      this.dot(s.x, s.y, COLOR_MANUAL);
    }
    if (this.measPoints.length === 2) {
      const a = this.scene.project(this.measPoints[0]);
      const b = this.scene.project(this.measPoints[1]);
      const mm = Math.round(this.measPoints[0].distanceTo(this.measPoints[1]) / S);
      this.dimLine(a.x, a.y, b.x, b.y, `${mm} mm`, COLOR_MANUAL);
    }
    // điểm bám đang hover
    if (this.mode === 'manual' && this.hoverSnap) {
      const s = this.scene.project(this.hoverSnap);
      ctx.beginPath();
      ctx.arc(s.x, s.y, 6, 0, Math.PI * 2);
      ctx.strokeStyle = COLOR_MANUAL;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  private dot(x: number, y: number, color: string): void {
    const { ctx } = this;
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }

  /** Đường kích thước có mũi tên 2 đầu + nhãn nền (port drawDimLine2D). */
  private dimLine(x1: number, y1: number, x2: number, y2: number, text: string, color: string): void {
    const { ctx } = this;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len < 4) return;
    const al = 7;
    const ang = Math.atan2(dy, dx);
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.moveTo(x1, y1);
    ctx.lineTo(x1 + al * Math.cos(ang - 0.45), y1 + al * Math.sin(ang - 0.45));
    ctx.moveTo(x1, y1);
    ctx.lineTo(x1 + al * Math.cos(ang + 0.45), y1 + al * Math.sin(ang + 0.45));
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - al * Math.cos(ang - 0.45), y2 - al * Math.sin(ang - 0.45));
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - al * Math.cos(ang + 0.45), y2 - al * Math.sin(ang + 0.45));
    ctx.stroke();
    this.label((x1 + x2) / 2, (y1 + y2) / 2, text, color);
  }

  /** Nhãn chữ có nền trắng, căn giữa tại (tx, ty). */
  private label(tx: number, ty: number, text: string, color: string, px = 12): void {
    const { ctx } = this;
    ctx.font = `bold ${px}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const tw = ctx.measureText(text).width;
    const h = px + 6;
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.fillRect(tx - tw / 2 - 4, ty - h / 2, tw + 8, h);
    ctx.fillStyle = color;
    ctx.fillText(text, tx, ty);
  }
}

/** 26 snap point của 1 khung bao: 8 góc + 12 giữa cạnh + 6 tâm mặt. */
function snapPoints(bb: Box3): Vector3[] {
  const { min: mn, max: mx } = bb;
  const cx = (mn.x + mx.x) / 2;
  const cy = (mn.y + mx.y) / 2;
  const cz = (mn.z + mx.z) / 2;
  return [
    new Vector3(mn.x, mn.y, mn.z), new Vector3(mx.x, mn.y, mn.z),
    new Vector3(mn.x, mx.y, mn.z), new Vector3(mx.x, mx.y, mn.z),
    new Vector3(mn.x, mn.y, mx.z), new Vector3(mx.x, mn.y, mx.z),
    new Vector3(mn.x, mx.y, mx.z), new Vector3(mx.x, mx.y, mx.z),
    new Vector3(cx, mn.y, mn.z), new Vector3(cx, mx.y, mn.z),
    new Vector3(cx, mn.y, mx.z), new Vector3(cx, mx.y, mx.z),
    new Vector3(mn.x, cy, mn.z), new Vector3(mx.x, cy, mn.z),
    new Vector3(mn.x, cy, mx.z), new Vector3(mx.x, cy, mx.z),
    new Vector3(mn.x, mn.y, cz), new Vector3(mx.x, mn.y, cz),
    new Vector3(mn.x, mx.y, cz), new Vector3(mx.x, mx.y, cz),
    new Vector3(mn.x, cy, cz), new Vector3(mx.x, cy, cz),
    new Vector3(cx, mn.y, cz), new Vector3(cx, mx.y, cz),
    new Vector3(cx, cy, mn.z), new Vector3(cx, cy, mx.z),
  ];
}
